import { type PointerEvent as ReactPointerEvent, useRef, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { Tab } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab';
import { TabList } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab-list';
import { TabPanel } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab-panel';

import { JsmPlaygroundNavigation } from './jsm-playground-navigation';
import { teamNavigationConfig, teamTabs, type TeamTabValue } from './jsm-portal-playground-data';
import { jsmPortalPlaygroundMessages as messages } from './jsm-portal-playground-messages';

type UniformJsmPortalPlaygroundProps = {
	initialActiveTab?: TeamTabValue;
};

export const UniformJsmPortalPlayground = ({
	initialActiveTab = 'it-support',
}: UniformJsmPortalPlaygroundProps) => {
	const intl = useIntl();
	const [activeTab, setActiveTab] = useState<TeamTabValue>(initialActiveTab);
	const [isCursorActive, setIsCursorActive] = useState(false);
	const cursorRef = useRef<HTMLDivElement>(null);
	const isPointerInsidePreviewRef = useRef(false);

	const updateCursorPosition = (event: ReactPointerEvent<HTMLElement>) => {
		const cursor = cursorRef.current;

		if (!cursor) {
			return;
		}

		const sectionBounds = event.currentTarget.getBoundingClientRect();
		cursor.style.setProperty('--cursor-x', `${event.clientX - sectionBounds.left}px`);
		cursor.style.setProperty('--cursor-y', `${event.clientY - sectionBounds.top}px`);
	};

	const handlePreviewPointerEnter = (event: ReactPointerEvent<HTMLDivElement>) => {
		isPointerInsidePreviewRef.current = true;
		updateCursorPosition(event);
		setIsCursorActive(true);
	};

	const handlePreviewPointerLeave = () => {
		isPointerInsidePreviewRef.current = false;
		setIsCursorActive(false);
	};

	const handlePreviewFocusChange = () => {
		if (!isPointerInsidePreviewRef.current) {
			setIsCursorActive(false);
		}
	};

	const renderCursor = () => (
		<div
			ref={cursorRef}
			aria-hidden="true"
			data-custom-cursor="you"
			css={[customCursorStyles, isCursorActive && customCursorVisibleStyles]}
		>
			<svg
				css={customCursorPointerStyles}
				width="24"
				height="48"
				viewBox="0 0 23 23"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M7.73535 22.6201L0.000042 0.000059L22.5846 7.77092L12.6866 12.7218L7.73535 22.6201Z"
					fill="currentColor"
				/>
			</svg>
			<div css={customCursorLabelStyles}>{intl.formatMessage(messages.customCursorLabel)}</div>
		</div>
	);

	return (
		<section css={playgroundSectionStyles} aria-label={intl.formatMessage(messages.sectionLabel)}>
			<div css={tabListWrapperStyles}>
				<TabList
					activeTabId={activeTab}
					aria-label={intl.formatMessage(messages.serviceTeamTabListLabel)}
				>
					{teamTabs.map((teamTab, index) => (
						<Tab
							key={teamTab.value}
							tabId={teamTab.value}
							index={index}
							title={intl.formatMessage(teamTab.label)}
							isActive={teamTab.value === activeTab}
							onTabSelect={(id) => setActiveTab(id as TeamTabValue)}
							tabSize="small"
						/>
					))}
				</TabList>
			</div>

			<div css={previewOuterStyles}>
				{renderCursor()}
				<div
					css={previewCardStyles}
					onBlur={handlePreviewFocusChange}
					onFocus={handlePreviewFocusChange}
					onPointerEnter={handlePreviewPointerEnter}
					onPointerLeave={handlePreviewPointerLeave}
					onPointerMove={updateCursorPosition}
				>
					<div css={panelStackStyles}>
						{teamTabs.map((teamTab) => (
							<TabPanel
								key={teamTab.value}
								id={teamTab.value}
								isActive={teamTab.value === activeTab}
							>
								<div css={teamPreviewStyles}>
									<JsmPlaygroundNavigation
										config={teamNavigationConfig[teamTab.value]}
										teamName={intl.formatMessage(teamTab.label)}
									/>
									<div css={blankPreviewStyles} />
								</div>
							</TabPanel>
						))}
					</div>
				</div>
			</div>
		</section>
	);
};

export default UniformJsmPortalPlayground;

const playgroundSectionStyles = css({
	position: 'relative',
	boxSizing: 'border-box',
	width: '100%',
	overflowX: 'clip',
	paddingBlockStart: token('space.400'),
	paddingBlockEnd: token('space.600'),
	paddingInline: token('space.300'),
	backgroundColor: token('elevation.surface'),
	[media.above.md]: {
		paddingInline: token('space.600'),
	},
});

const customCursorStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	zIndex: 5,
	width: 0,
	height: 0,
	opacity: 0,
	pointerEvents: 'none',
	transform: 'translate3d(var(--cursor-x, 0px), var(--cursor-y, 0px), 0)',
	transition: 'opacity 120ms ease',
	'@media (pointer: coarse)': {
		display: 'none',
	},
});

const customCursorVisibleStyles = css({
	opacity: 1,
});

const customCursorLabelStyles = css({
	position: 'absolute',
	insetBlockStart: '16px',
	insetInlineStart: '25px',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	paddingBlock: token('space.050'),
	paddingInline: token('space.150'),
	borderRadius: token('radius.full'),
	backgroundColor: token('color.background.warning.bold'),
	color: token('color.text'),
	font: token('font.heading.medium'),
	fontWeight: token('font.weight.semibold'),
	whiteSpace: 'nowrap',
});

const customCursorPointerStyles = css({
	position: 'absolute',
	insetBlockStart: '-16px',
	insetInlineStart: 0,
	width: '24px',
	height: '48px',
	transform: 'scale(1.3)',
	transformOrigin: '0 0',
	color: token('color.background.warning.bold'),
});

const tabListWrapperStyles = css({
	display: 'flex',
	justifyContent: 'center',
	width: 'fit-content',
	maxWidth: '100%',
	marginInline: 'auto',
	marginBlockEnd: token('space.400'),
	// Override the shared TabList background and border for the playground's outlined theme,
	// keeping styling changes scoped to this component without modifying the shared design pattern.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> div': {
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		border: `1px solid ${wacToken('color.primary.black')} !important`,
		[media.below.sm]: {
			backgroundColor: 'transparent !important',
			border: 'none !important',
		},
	},
	// Keep tab horizontal padding compact (20px) so the switcher is ~480px wide matching the prototype
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'a[role="tab"]': {
		paddingInline: `${token('space.250')} !important`,
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: token('font.weight.semibold'),
	},
});

const panelStackStyles = css({
	display: 'grid',
	gridTemplateColumns: '1fr',
	gridTemplateRows: '1fr',
	width: '100%',
	height: '100%',
});

const previewOuterStyles = css({
	position: 'relative',
	width: '100%',
	maxWidth: '1168px',
	marginInline: 'auto',
});

const previewCardStyles = css({
	position: 'relative',
	zIndex: 1,
	boxSizing: 'border-box',
	overflow: 'hidden',
	width: '100%',
	height: '750px',
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface'),
	boxShadow: token('elevation.shadow.overlay'),
	cursor: 'none',
	'@media (pointer: coarse)': {
		cursor: 'auto',
	},
});

const blankPreviewStyles = css({
	flex: '1 1 auto',
	width: '100%',
	backgroundColor: token('elevation.surface'),
});

const teamPreviewStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	height: '100%',
});
